import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  MapPin, Calendar, Star, ArrowLeft, Check,
  MessageCircle, ChevronDown, ChevronRight, X, Play, Volume2, VolumeX, Users,
} from "lucide-react";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import { getExperienceDetail, clearExperienceDetail } from "../../redux/reducers/Experiences/ExperiencesSlice";
import { AppDispatch, RootState } from "../../redux/store";

// ── TYPES ─────────────────────────────────────────────────
// Add these to your ExperiencesSlice.tsx:
//
// interface ExperiencePrice {
//   audience_type: string;
//   label: string;
//   currency: string;
//   regular_price: string | null;
//   early_bird_price: string | null;
//   early_bird_deadline: string | null;
//   active_price: string | null;
//   has_early_bird: boolean;
// }
//
// interface ExperienceVideo {
//   url: string;
//   title: string | null;
// }
//
// Add to ExperienceDetail:
//   prices?: ExperiencePrice[];
//   videos?: ExperienceVideo[];
//   from_price?: string | null;
//   from_currency?: string | null;

const CURRENCY_SYMBOLS: Record<string, string> = { USD: "$", PEN: "S/", EUR: "€" };
const CURRENCY_NAMES: Record<string, string> = { USD: "US Dollar", PEN: "Peruvian Sol", EUR: "Euro" };



const AUDIENCE_ICONS: Record<string, string> = {
  standard:      "🌍",
  resident:      "🏠",
  international: "✈️",
  local:         "🇵🇪",
};

const whatsappNumber = "51924634308"; // your WhatsApp number with country code, no +



const formatPrice = (amount: string | null | undefined, currency = "USD"): string => {
  if (!amount) return "TBA";
  const symbol = CURRENCY_SYMBOLS[currency] || "$";
  const num = parseFloat(amount);
  return `${symbol}${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)}`;
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric",
  });
};

// ── VIDEO MODAL ───────────────────────────────────────────
const VideoModal: React.FC<{ src: string; title?: string | null; onClose: () => void }> = ({ src, title, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-sm lg:max-w-lg mx-4 flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          {title && <p className="text-white/70 text-sm font-medium truncate pr-4">{title}</p>}
          <button onClick={onClose} className="ml-auto w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Video */}
        <div className="relative rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: "9/16", maxHeight: "78vh", margin: "0 auto", width: "auto" }}>
          <video ref={videoRef} className="w-full h-full object-contain" autoPlay loop playsInline style={{ maxHeight: "78vh" }}>
            <source src={src} type="video/mp4" />
          </video>
          <button
            onClick={() => { if (videoRef.current) { videoRef.current.muted = !videoRef.current.muted; setMuted(videoRef.current.muted); } }}
            className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          >
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        </div>
        <p className="text-center text-white/30 text-xs mt-3">Tap outside to close</p>
      </div>
    </div>
  );
};

// ── PRICING TABLE ─────────────────────────────────────────
const PricingTable: React.FC<{ prices: any[]; currency: string }> = ({ prices, currency }) => {
  if (!prices?.length) return null;

  const hasAnyEarlyBird = prices.some(p => p.has_early_bird);

  return (
    <section className="mb-6">
      <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-3 px-4 sm:px-0">
        Pricing by Audience
      </h2>
      <div className="mx-4 sm:mx-0 overflow-hidden rounded-2xl shadow-sm border border-gray-100 dark:border-neutral-700">
        {/* Header row */}
        <div className="grid bg-gray-50 dark:bg-neutral-800 border-b border-gray-200 dark:border-neutral-700 px-4 py-2.5"
          style={{ gridTemplateColumns: hasAnyEarlyBird ? "1fr 1fr 1fr" : "1fr 1fr" }}>
          <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Audience</span>
          <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center">Regular</span>
          {hasAnyEarlyBird && (
            <span className="text-[10px] font-bold text-[#f5694b] uppercase tracking-wider text-center">🐦 Early Bird</span>
          )}
        </div>

        {/* Price rows */}
        <div className="divide-y divide-gray-100 dark:divide-neutral-700 bg-white dark:bg-neutral-800">
          {prices.map((price, i) => {
            const cur = price.currency || currency;
            const isActive = price.has_early_bird;
            return (
              <div
                key={i}
                className={`grid items-center px-4 py-3.5 ${isActive ? "bg-[#f5694b]/3" : ""}`}
                style={{ gridTemplateColumns: hasAnyEarlyBird ? "1fr 1fr 1fr" : "1fr 1fr" }}
              >
                {/* Audience */}
                <div className="flex items-center gap-2">
                  <span className="text-base">{AUDIENCE_ICONS[price.audience_type] || "👤"}</span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">{price.label}</p>
                    <p className="text-[10px] text-gray-400">{CURRENCY_NAMES[cur] || cur}</p>
                  </div>
                </div>

                {/* Regular price */}
                <div className="text-center">
                  <span className={`text-sm font-semibold ${isActive ? "text-gray-400 line-through" : "text-gray-900 dark:text-white"}`}>
                    {formatPrice(price.regular_price, cur)}
                  </span>
                </div>

                {/* Early bird price */}
                {hasAnyEarlyBird && (
                  <div className="text-center">
                    {price.has_early_bird ? (
                      <div>
                        <span className="text-sm font-bold text-[#f5694b]">
                          {formatPrice(price.early_bird_price, cur)}
                        </span>
                        {price.early_bird_deadline && (
                          <p className="text-[9px] text-gray-400 mt-0.5">
                            until {formatDate(price.early_bird_deadline)}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-neutral-900/40 border-t border-gray-100 dark:border-neutral-700">
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            All prices are per person. Contact us to confirm your rate.
          </p>
        </div>
      </div>
    </section>
  );
};

// ── TRIP PROFILE ──────────────────────────────────────────
const TripProfileCard: React.FC<{
  travelStyle: string; travelStyleDetail: string;
  accommodation: string; accommodationDetail: string;
  physicalRating: string; physicalRatingDetail: string;
  tripType: string; tripTypeDetail: string;
  ageRequirement: string; ageRequirementDetail: string;
}> = (props) => {
  const items = [
    { label: "Travel Style",    value: props.travelStyle,    detail: props.travelStyleDetail,    icon: "🥾" },
    { label: "Accommodation",   value: props.accommodation,  detail: props.accommodationDetail,  icon: "🏨" },
    { label: "Physical Rating", value: props.physicalRating, detail: props.physicalRatingDetail, icon: "💪" },
    { label: "Trip Type",       value: props.tripType,       detail: props.tripTypeDetail,       icon: "👥" },
    { label: "Age Requirement", value: props.ageRequirement, detail: props.ageRequirementDetail, icon: "🎓" },
  ];
  return (
    <div className="container mx-auto px-4 mb-8">
      <div className="bg-white dark:bg-neutral-800 rounded-2xl p-5 sm:p-8 shadow-sm">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-5 sm:mb-8">Is This Trip for Me?</h2>
        <div className="divide-y divide-gray-100 dark:divide-neutral-700">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-start gap-3 sm:gap-6 py-4 sm:py-5 first:pt-0 last:pb-0">
              <span className="text-xl sm:text-2xl w-7 sm:w-9 flex-shrink-0 mt-0.5">{item.icon}</span>
              <div className="w-24 sm:w-36 flex-shrink-0">
                <span className="text-[10px] sm:text-xs font-semibold text-[#f5694b] uppercase tracking-wider">{item.label}</span>
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{item.value}</span>
                {item.detail && (
                  <span className="text-xs sm:text-sm text-gray-500 block sm:inline sm:ml-3">
                    <span className="hidden sm:inline">— </span>{item.detail}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── ITINERARY ─────────────────────────────────────────────
const ItineraryAccordion: React.FC<{ itinerary: { day: number; title: string; activities: string[] }[] }> = ({ itinerary }) => {
  const [openDays, setOpenDays] = useState<number[]>([]);
  const allOpen = openDays.length === itinerary.length;
  const toggle = (day: number) => setOpenDays(p => p.includes(day) ? p.filter(d => d !== day) : [...p, day]);

  return (
    <div className="container mx-auto px-4 mb-8">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-3xl font-bold text-gray-900 dark:text-white">Itinerary</h2>
        <button onClick={() => setOpenDays(allOpen ? [] : itinerary.map(d => d.day))}
          className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 dark:border-neutral-600 rounded-full text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
          <ChevronDown className={`w-3.5 sm:w-4 h-3.5 sm:h-4 transition-transform ${allOpen ? "rotate-180" : ""}`} />
          {allOpen ? "Hide all" : "Show all"}
        </button>
      </div>
      <div className="divide-y divide-gray-200 dark:divide-neutral-700 border-t border-b border-gray-200 dark:border-neutral-700">
        {itinerary.map((day) => {
          const isOpen = openDays.includes(day.day);
          return (
            <div key={day.day}>
              <button onClick={() => toggle(day.day)} className="w-full flex items-center justify-between py-4 sm:py-5 text-left hover:bg-gray-50 dark:hover:bg-neutral-800/50 transition-colors px-1 sm:px-2">
                <span className="text-sm sm:text-base text-gray-900 dark:text-white pr-3">Day {day.day} · <strong>{day.title}</strong></span>
                <ChevronDown className={`w-4 sm:w-5 h-4 sm:h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="pb-5 sm:pb-6 px-1 sm:px-2">
                  {day.activities.length > 0 ? (
                    <ul className="space-y-2.5 mt-1">
                      {day.activities.map((a, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-gray-600 dark:text-gray-400 text-xs sm:text-sm leading-relaxed">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#f5694b] mt-1.5 flex-shrink-0" />{a}
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-sm text-gray-400 italic">Details coming soon.</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── INCLUSIONS ────────────────────────────────────────────
const IncludedSection: React.FC<{ included: string[]; notIncluded: string[] }> = ({ included, notIncluded }) => {
  if (!included.length && !notIncluded.length) return null;
  return (
    <div className="container mx-auto px-4 mb-8">
      <h2 className="text-lg sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Inclusions and activities</h2>
      <div className="bg-gray-50 dark:bg-neutral-800 rounded-2xl p-5 sm:p-8 lg:p-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10">
          {included.length > 0 && (
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-4">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-gray-900 dark:border-white flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 sm:w-4 h-3 sm:h-4 text-gray-900 dark:text-white" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">Included</h3>
              </div>
              <ul className="space-y-2.5">
                {included.map((item, i) => <li key={i} className="flex items-start gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300"><span className="text-gray-400 mt-0.5 flex-shrink-0">•</span>{item}</li>)}
              </ul>
            </div>
          )}
          {notIncluded.length > 0 && (
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-4">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-gray-400 flex items-center justify-center flex-shrink-0">
                  <X className="w-3 sm:w-4 h-3 sm:h-4 text-gray-400" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">Not included</h3>
              </div>
              <ul className="space-y-2.5">
                {notIncluded.map((item, i) => <li key={i} className="flex items-start gap-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400"><span className="text-gray-300 mt-0.5 flex-shrink-0">•</span>{item}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── UPCOMING DATES ────────────────────────────────────────
const UpcomingDates: React.FC<{ dates: string[]; onBook: () => void }> = ({ dates, onBook }) => {

  
  if (!dates.length) return null;
  return (
    <div className="container mx-auto px-4 mb-8">
      <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 sm:px-8 py-4 sm:py-6 border-b border-gray-100 dark:border-neutral-700">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">Upcoming Departures</h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{dates.length} departure{dates.length !== 1 ? "s" : ""} available</p>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-neutral-700">
          {dates.map((date, i) => (
            <div key={i} className="flex items-center gap-3 sm:gap-6 px-5 sm:px-8 py-3.5 sm:py-5 hover:bg-gray-50 dark:hover:bg-neutral-750 transition-colors group">
              <span className="text-xs font-bold text-gray-300 w-5 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#f5694b]/10 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#f5694b]" />
              </div>
              <div className="flex-1 min-w-0"><span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{date}</span></div>
              <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-green-600 bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-full flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />Available
              </span>
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#f5694b] transition-colors flex-shrink-0" />
            </div>
          ))}
        </div>
        <div className="px-5 sm:px-8 py-4 sm:py-5 bg-gray-50 dark:bg-neutral-900/40 border-t border-gray-100 dark:border-neutral-700">
         <button
  onClick={onBook}
  className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-[#f5694b] hover:text-[#e2583d] transition-colors"
>
  <MessageCircle className="w-3.5 sm:w-4 h-3.5 sm:h-4" />Contact us to reserve your spot
</button>
        </div>
      </div>
    </div>
  );
};

// ── WHAT TO BRING ─────────────────────────────────────────
const WhatToBring: React.FC<{ items: string[] }> = ({ items }) => (
  <div className="container mx-auto px-4 mb-8">
    <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 sm:px-8 py-4 sm:py-6 border-b border-gray-100 dark:border-neutral-700">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">What to Bring</h2>
      </div>
      <div className="px-5 sm:px-8 py-5 sm:py-7">
        {items.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-0">
            {items.map((item, i) => (
              <div key={i} className="flex items-center gap-2 sm:gap-3 py-2.5 sm:py-3 border-b border-gray-100 dark:border-neutral-700 last:border-0 pr-4 sm:pr-6">
                <Check className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#f5694b] flex-shrink-0" />
                <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-tight">{item}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-3 py-3">
            <span className="text-2xl">🧳</span>
            <p className="text-xs sm:text-sm text-gray-400">Packing list will be shared closer to the trip date.</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ── MAIN PAGE ─────────────────────────────────────────────
const ExperienceDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { experienceDetail: experience, detailLoading } = useSelector((state: RootState) => state.experiences);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeVideo, setActiveVideo] = useState<{ url: string; title?: string | null } | null>(null);

  useEffect(() => {
    if (slug) dispatch(getExperienceDetail(slug));
    return () => { dispatch(clearExperienceDetail()); };
  }, [slug, dispatch]);

  useEffect(() => { setSelectedImage(0); }, [experience?.slug]);

  if (detailLoading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f5694b] mx-auto mb-4" />
        <p className="text-sm text-gray-500">Loading experience...</p>
      </div>
    </div>
  );

  if (!experience) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center">
        <p className="text-sm text-gray-500 mb-4">Experience not found.</p>
        <Link to="/experiences" className="text-sm text-[#f5694b] hover:underline">← Back to Experiences</Link>
      </div>
    </div>
  );

  const currency = experience.currency || "USD";
  const fallback = "https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg";
  const coverImage = experience.cover_image || fallback;
  const galleryImages: string[] = experience.images?.length > 0 ? experience.images : [];
  const allImages = [coverImage, ...galleryImages.filter((img: string) => img !== coverImage)];

  // Videos — new array, fallback to legacy video_url
  const videos: { url: string; title?: string | null }[] =
    experience.videos && experience.videos.length > 0
      ? experience.videos
      : experience.video_url
      ? [{ url: experience.video_url, title: null }]
      : [];

  // Prices — new array, fallback to legacy fields
  const prices: any[] =
    experience.prices && experience.prices.length > 0
      ? experience.prices
      : experience.regular_price || experience.early_bird_price
      ? [{
          audience_type:       "standard",
          label:               "Standard",
          currency,
          regular_price:       experience.regular_price,
          early_bird_price:    experience.early_bird_price,
          early_bird_deadline: experience.early_bird_deadline,
          active_price:        experience.early_bird_price || experience.regular_price,
          has_early_bird:      !!experience.early_bird_price,
        }]
      : [];

  // Lowest active price for the sticky bar / info card
  const lowestPrice = prices.reduce((min: any, p: any) => {
    if (!p.active_price) return min;
    if (!min || parseFloat(p.active_price) < parseFloat(min.active_price)) return p;
    return min;
  }, null);

  const displayPrice = lowestPrice?.active_price || experience.early_bird_price || experience.regular_price;
  const displayCurrency = lowestPrice?.currency || currency;

  // ← ADD HERE
const whatsappNumber = "51924634308";
const handleBookWhatsApp = () => {
  const message = encodeURIComponent(
    `Hi! I'm interested in booking the "${experience.title}" trip. Could you give me more details?`
  );
  window.open(`https://wa.me/${whatsappNumber}?text=${message}`, "_blank");
};

  // Gallery items: photos + video tiles
  const imageItems = allImages.map((src, index) => ({ type: "image" as const, src, index }));

  return (
    <div className="nc-ExperienceDetailPage pb-24 sm:pb-0">
      <BgGlassmorphism />

      {/* Video Modal */}
      {activeVideo && (
        <VideoModal src={activeVideo.url} title={activeVideo.title} onClose={() => setActiveVideo(null)} />
      )}

      {/* Back */}
      <div className="container mx-auto px-4 py-4 sm:py-6">
        <Link to="/experiences" className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-[#f5694b] transition-colors">
          <ArrowLeft className="w-4 sm:w-5 h-4 sm:h-5" /><span>Back to Experiences</span>
        </Link>
      </div>

      {/* ── HERO ── */}
      <div className="container mx-auto px-4 mb-8 sm:mb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">

          {/* Left: main image */}
          <div className="relative h-56 sm:h-[400px] lg:h-[600px] rounded-2xl overflow-hidden">
            <img src={allImages[selectedImage]} alt={experience.title} className="w-full h-full object-cover" />
          </div>

          {/* Right: gallery strip + info card */}
          <div className="space-y-3 sm:space-y-4">

            {/* Gallery strip — images + video tiles, horizontal scroll on mobile */}
            {(imageItems.length > 1 || videos.length > 0) && (
              <div
                className="flex gap-2 overflow-x-auto pb-1 lg:grid lg:grid-cols-3 lg:gap-3 lg:overflow-visible"
                style={{ scrollbarWidth: "none" }}
              >
                {/* Image thumbnails */}
                {imageItems.map((item, i) => (
                  <div
                    key={`img-${i}`}
                    onClick={() => setSelectedImage(item.index)}
                    className={`flex-shrink-0 w-20 h-14 sm:w-auto sm:h-20 lg:h-28 rounded-xl overflow-hidden cursor-pointer transition-all ${
                      selectedImage === item.index ? "ring-2 sm:ring-4 ring-[#f5694b]" : "opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img src={item.src} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}

                {/* Video tiles */}
                {videos.map((video, i) => (
                  <div
                    key={`vid-${i}`}
                    onClick={() => setActiveVideo(video)}
                    className="flex-shrink-0 w-20 h-14 sm:w-auto sm:h-20 lg:h-28 rounded-xl overflow-hidden cursor-pointer group bg-black relative"
                  >
                    {/* Blurred poster */}
                    <img src={allImages[0]} alt="" className="w-full h-full object-cover opacity-40 scale-110 blur-[1px]" />
                    {/* Play button */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200">
                        <Play className="w-4 sm:w-5 h-4 sm:h-5 text-[#f5694b] ml-0.5" />
                      </div>
                      {video.title && (
                        <span className="text-white text-[8px] sm:text-[10px] font-semibold bg-black/60 px-1.5 py-0.5 rounded-full max-w-[70px] truncate">
                          {video.title}
                        </span>
                      )}
                    </div>
                    {/* Video count badge if multiple */}
                    {videos.length > 1 && i === 0 && (
                      <div className="absolute top-1 right-1 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                        {videos.length} videos
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Info card */}
            <div className="bg-white dark:bg-neutral-800 rounded-2xl p-4 sm:p-6 lg:p-7 shadow-xl">

              {/* Badges */}
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <span className="px-2.5 sm:px-3 py-0.5 sm:py-1 bg-[#f5694b]/10 text-[#f5694b] rounded-full text-[11px] sm:text-xs font-semibold">{experience.category}</span>
                <span className="px-2.5 sm:px-3 py-0.5 sm:py-1 bg-gray-100 dark:bg-neutral-700 rounded-full text-[11px] sm:text-xs font-semibold text-gray-700 dark:text-gray-300">{experience.difficulty}</span>
              </div>

              {/* Title */}
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 leading-tight">{experience.title}</h1>

              {/* Location + Rating */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-4 sm:mb-5 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1 sm:gap-1.5">
                  <MapPin className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-[#f5694b]" /><span>{experience.location}</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-1.5">
                  <Star className="w-3.5 sm:w-4 h-3.5 sm:h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-semibold text-gray-900 dark:text-white">{experience.rating}</span>
                  <span>({experience.review_count} reviews)</span>
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-5 pb-4 sm:pb-5 border-b border-gray-100 dark:border-neutral-700">
                <div className="bg-gray-50 dark:bg-neutral-700 rounded-xl px-3 sm:px-4 py-2 sm:py-3">
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5">Duration</p>
                  <p className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">{experience.duration}</p>
                </div>
                <div className="bg-gray-50 dark:bg-neutral-700 rounded-xl px-3 sm:px-4 py-2 sm:py-3">
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5">Group size</p>
                  <p className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">{experience.group_size}</p>
                </div>
              </div>

              {/* Price summary — shows lowest/from price */}
              {displayPrice && (
                <div className="mb-4 sm:mb-5">
                  {prices.length > 1 && (
                    <p className="text-[10px] text-gray-400 mb-1 flex items-center gap-1">
                      <Users className="w-3 h-3" /> {prices.length} pricing tiers available — see full table below
                    </p>
                  )}
                  <div className="flex items-baseline justify-between">
                    <div>
                      <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">From</span>
                      <div className="flex items-baseline gap-1.5 sm:gap-2 mt-0.5">
                        <span className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                          {CURRENCY_SYMBOLS[displayCurrency] || "$"}{parseFloat(displayPrice).toFixed(0)}
                        </span>
                        <span className="text-xs sm:text-sm text-gray-500">{displayCurrency} / person</span>
                      </div>
                    </div>
                    {/* Early bird badge on lowest price */}
                    {lowestPrice?.has_early_bird && (
                      <span className="text-[10px] font-bold text-[#f5694b] bg-[#f5694b]/10 px-2 py-1 rounded-full">
                        🐦 Early bird
                      </span>
                    )}
                  </div>
                </div>
              )}

             
             <button
  onClick={handleBookWhatsApp}
  className="hidden sm:flex w-full items-center justify-center gap-2 px-6 py-3.5 sm:py-4 bg-[#f5694b] hover:bg-[#e2583d] text-white font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] text-sm sm:text-base"
>
  <MessageCircle className="w-4 sm:w-5 h-4 sm:h-5" /><span>Book this trip</span>
</button>
              <p className="hidden sm:block text-xs text-center text-gray-400 dark:text-gray-500 mt-2">No payment required to enquire</p>
            </div>
          </div>
        </div>
      </div>

      <UpcomingDates dates={experience.next_dates || []} onBook={handleBookWhatsApp} />

      {/* About */}
      <div className="container mx-auto px-4 mb-8 sm:mb-12">
        <h2 className="text-lg sm:text-3xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-6">About this trip</h2>
        <div className="bg-gray-50 dark:bg-neutral-800 rounded-2xl p-5 sm:p-8 lg:p-10">
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm sm:text-lg">
            {experience.long_description || experience.description}
          </p>
        </div>
      </div>

      {/* Pricing Table — full audience breakdown */}
      <div className="container mx-auto px-4 mb-8 sm:mb-12">
        <PricingTable prices={prices} currency={currency} />
      </div>

      <TripProfileCard
        travelStyle={experience.travel_style} travelStyleDetail={experience.travel_style_detail}
        accommodation={experience.accommodation} accommodationDetail={experience.accommodation_detail}
        physicalRating={experience.physical_rating} physicalRatingDetail={experience.physical_rating_detail}
        tripType={experience.trip_type} tripTypeDetail={experience.trip_type_detail}
        ageRequirement={experience.age_requirement} ageRequirementDetail={experience.age_requirement_detail}
      />

      

      {experience.itinerary?.length > 0 && <ItineraryAccordion itinerary={experience.itinerary} />}

      <IncludedSection included={experience.included || []} notIncluded={experience.not_included || []} />

      <WhatToBring items={experience.what_to_bring || []} />

      {/* Important Notes */}
      {(experience.important_notes ?? []).length > 0 && (
        <div className="container mx-auto px-4 mb-8">
          <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 sm:px-8 py-4 sm:py-6 border-b border-gray-100 dark:border-neutral-700">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span className="text-[#f5694b]">📋</span>Important Notes
              </h2>
            </div>
            <div className="px-5 sm:px-8 py-5 sm:py-7">
              <ul className="space-y-2.5 sm:space-y-3">
                {(experience.important_notes ?? []).map((note: string, i: number) => (
                  <li key={i} className="flex items-start gap-2.5 sm:gap-3 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#f5694b] mt-1.5 sm:mt-2 flex-shrink-0" />{note}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* CTA Banner */}
      <div className="bg-gradient-to-br from-[#f5694b] to-[#e2583d] py-10 sm:py-16 mb-8 sm:mb-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">Ready for This Adventure?</h2>
          <p className="text-white/90 text-sm sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">
            Contact us today to reserve your spot on this incredible journey!
          </p>
      
<button
  onClick={handleBookWhatsApp}
  className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white text-[#f5694b] hover:bg-gray-100 font-semibold rounded-full shadow-xl transition-all duration-300 hover:scale-105 text-sm sm:text-base"
>
  <MessageCircle className="w-4 sm:w-5 h-4 sm:h-5" /><span>Get in Touch</span>
</button>
        </div>
      </div>

     
      {displayPrice && (
        <div className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-white dark:bg-neutral-900 border-t border-gray-200 dark:border-neutral-700 px-4 py-3 flex items-center gap-3 shadow-2xl">
          <div className="flex-1 min-w-0">
            <p className="text-[9px] text-gray-400 uppercase tracking-wide">From</p>
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {CURRENCY_SYMBOLS[displayCurrency] || "$"}{parseFloat(displayPrice).toFixed(0)}
              </span>
              <span className="text-[10px] text-gray-500">{displayCurrency} / person</span>
            </div>
            {prices.length > 1 && (
              <p className="text-[9px] text-gray-400">{prices.length} tiers available</p>
            )}
          </div>
     
<button
  onClick={handleBookWhatsApp}
  className="flex-shrink-0 flex items-center gap-1.5 px-5 py-3 bg-[#f5694b] hover:bg-[#e2583d] text-white font-bold rounded-full shadow-lg text-sm"
>
  <MessageCircle className="w-4 h-4" />Book now
</button>
        </div>
      )}
    </div>
  );
};

export default ExperienceDetailPage;